var CartSyncController = (function () {
  // ─── Config ───────────────────────────────────────────────────────────────

  const config = {
    enabled: true,
    storageKey: 'imin__cart_items',
    syncDebounceMs: 400,
    identity_key: 'big__user_identity',
    cartSyncKey: 'cart_synced',
    welcomeGiftVariantId: 45811900514490,
  };

  // ─── Internal State ───────────────────────────────────────────────────────

  let _syncDebounceTimer = null;
  let _isSyncing = false;
  let _listeners = {};
  let _syncPromise = null;

  // ─── Private Helpers ──────────────────────────────────────────────────────

  function _emit(event, payload) {
    (_listeners[event] || []).forEach((fn) => fn(payload));
  }

  function _saveLocal(items) {
    try {
      localStorage.setItem(config.storageKey, JSON.stringify(items));
      _emit('change', items);
    } catch (e) {
      console.warn('[CartSync] localStorage write failed:', e);
    }
  }

  /**
   *  transform cart items from shopify to map the required properties to save locally and remotely
   *
   */
  function _transformCartItems(items = []) {
    return _combineLines(
      items.map((item) => {
        return {
          quantity: item.quantity,
          variantId: item.variant_id,
          productId: item.product_id,
          properties: item.properties,
        };
      }),
    );
  }

  function _combineLines(items = []) {
    const map = new Map();

    items.forEach((item) => {
      const key = _lineKey(item);
      if (map.has(key)) {
        map.get(key).quantity += item.quantity;
      } else {
        map.set(key, { ...item });
      }
    });

    return Array.from(map.values());
  }

  function _cleanProperties(properties) {
    return Object.fromEntries(
      Object.entries(properties || {}).filter(
        ([_, v]) => v !== '' && v !== null && v !== undefined,
      ),
    );
  }

  // Each variant is its own line — composite key prevents collisions
  function _lineKey(item) {
    const properties = _cleanProperties(item.properties);
    const propsKey = Object.keys(properties)
      .sort()
      .map((k) => `${k}:${properties[k]}`)
      .join('|');

    return `${item.variantId}__${propsKey}`;
  }

  /**
   * Merge cart items for both local and remote
   * @param {*} localItems  - local items found in localStorage or shopify existing cart items
   * @param {*} remoteItems  - items in user account metafield
   * @param {*} incrementDuplicates - flag for merging behaviour. When set to `true` it will increment existing variant found in remote and local items that are not existed in remote will be added, for `false` it will just keep the current quantity and removed the items not registered in remote
   * @returns
   */
  function _mergeItems(localItems, remoteItems) {
    const map = new Map();

    remoteItems.forEach((item) => map.set(_lineKey(item), { ...item }));

    localItems.forEach((item) => {
      const key = _lineKey(item);

      if (map.has(key)) {
        // check if item is welcome gift otherwise increment quantity
        if (!_isWelcomeGift(item.variantId)) {
          map.get(key).quantity += item.quantity;
        }
      } else {
        map.set(key, { ...item });
      }
    });

    return Array.from(map.values());
  }

  function _getAccountCartItems() {
    return _combineLines(window?.shopifyUser?.cartItems || []);
  }

  function _getSectionToRender() {
    const sections = [
      {
        id: 'minicart-form',
        section: 'minicart-form',
      },
    ];

    const mainCartItems = document.getElementById('main-cart-items');
    if (mainCartItems?.dataset?.id) {
      sections.push({
        id: 'main-cart-items',
        section: mainCartItems.dataset.id,
      });
    }

    return sections;
  }

  function _isWelcomeGift(variantId) {
    return variantId === config.welcomeGiftVariantId;
  }

  // ─── API Calls ────────────────────────────────────────────────────────────
  async function _pushRemoteCartItems(customerId, items) {
    if (!window?.API_ENDPOINT) throw new Error('Api endpoint is missing!');

    const res = await fetch(
      `${window.API_ENDPOINT}/customer/${customerId}/cart`,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'x-api-key': window?.API_KEY,
        },
        body: JSON.stringify({
          items,
        }),
      },
    );
    if (!res.ok)
      throw new Error(`[CartSync] Failed to push remote cart: ${res.status}`);
    return res.json();
  }

  async function _validateCartItems(items = []) {
    if (!window?.API_ENDPOINT) throw new Error('Api endpoint is missing!');

    const res = await fetch(`${window.API_ENDPOINT}/customer/cart/validate`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'x-api-key': window?.API_KEY,
      },
      body: JSON.stringify({
        items,
      }),
    });
    if (!res.ok)
      throw new Error(`[CartSync] Failed to push remote cart: ${res.status}`);

    const body = await res.json();

    return body?.success ? body.data : items;
  }

  // ─── Shopify Cart API ─────────────────────────────────────────────────────

  async function _renderLogoutUI() {
    const res = await fetch('/?section_id=minicart-form');
    const body = await res.text();
    const parsedHTML = new DOMParser().parseFromString(body, 'text/html');

    const updatedMinicart = parsedHTML.querySelector('#minicart-form');
    const minicart = document.querySelector('#minicart-form');

    if (minicart && updatedMinicart) {
      minicart.innerHTML = updatedMinicart.innerHTML;
    }

    // update bubble count
    const cartCountEls = document.querySelectorAll('.cart-count.h-count');

    cartCountEls.forEach((el) => {
      el.textContent = 0;
    });

    // update drawer count
    const drawerCountEls = document.querySelectorAll(
      '.cart-count.cart-count-drawer',
    );

    drawerCountEls.forEach((el) => {
      el.textContent = '(0)';
    });

    // update free shipping bar
    _updateFreeShippingBar(0);
  }

  async function _replaceShopifyCart(items, logout = false) {
    return _withInternalUpdate(async () => {
      const clearRes = await fetch('/cart/clear.js', {
        method: 'POST',
      });

      if (!clearRes.ok)
        throw new Error(`[CartSync] /cart/clear.js failed: ${clearRes.status}`);

      // check for logout
      if (logout) {
        return await _renderLogoutUI();
      }

      if (!items.length) return;

      const res = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          items: items.map((i) => ({
            id: i.variantId,
            quantity: i.quantity,
            product_id: i.productId,
            properties: i?.properties || {},
          })),
          sections: _getSectionToRender().map((section) => section.section),
          sections_url: window.location.pathname,
        }),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(
          `[CartSync] Bulk /cart/add.js failed: ${err.description || res.status}`,
        );
      }

      const body = await res.json();

      // handle ui updates .e.g update bubble count, update minicart, etc.
      const resultItems = body?.items ?? [];
      const sections = body?.sections ?? [];
      _updateUI(resultItems, sections);
    });
  }

  async function _postCart(url, payload) {
    const res = await fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    });
    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      throw new Error(
        `[CartSync] ${url} failed: ${err.description || res.status}`,
      );
    }
    return res.json();
  }

  async function _applyShopifyCartDiff(items) {
    const currentLines = window?.shopifyUser?.existingCartItems || [];
    const targetMap = new Map(items.map((i) => [_lineKey(i), i]));
    const currentGroups = new Map();
    const updates = {};

    currentLines.forEach((line) => {
      const key = _lineKey(_transformCartItems([line])[0]);
      const group = currentGroups.get(key) || { quantity: 0, lineKeys: [] };
      group.quantity += line.quantity;
      if (!group.lineKeys.includes(line.key)) group.lineKeys.push(line.key);
      currentGroups.set(key, group);
    });

    currentGroups.forEach((group, key) => {
      const targetQuantity = targetMap.get(key)?.quantity ?? 0;
      if (group.quantity === targetQuantity) return;

      const [firstLineKey, ...otherLineKeys] = group.lineKeys;
      updates[firstLineKey] = targetQuantity;
      otherLineKeys.forEach((lineKey) => (updates[lineKey] = 0));
    });

    const itemsToAdd = items.filter((i) => !currentGroups.has(_lineKey(i)));
    const hasUpdates = Object.keys(updates).length > 0;

    if (!hasUpdates && !itemsToAdd.length) return;

    return _withInternalUpdate(async () => {
      const sectionsPayload = {
        sections: _getSectionToRender().map((section) => section.section),
        sections_url: window.location.pathname,
      };

      let cartLines = currentLines;
      let sections = {};

      if (hasUpdates) {
        const body = await _postCart('/cart/update.js', {
          updates,
          ...(itemsToAdd.length ? {} : sectionsPayload),
        });
        cartLines = body?.items ?? [];
        sections = body?.sections ?? {};
      }

      if (itemsToAdd.length) {
        const body = await _postCart('/cart/add.js', {
          items: itemsToAdd.map((i) => ({
            id: i.variantId,
            quantity: i.quantity,
            properties: i?.properties || {},
          })),
          ...sectionsPayload,
        });
        sections = body?.sections ?? {};

        const cartRes = await fetch('/cart.js');
        const cart = await cartRes.json();
        cartLines = cart?.items ?? [];
      }

      if (window.shopifyUser) window.shopifyUser.existingCartItems = cartLines;

      _updateUI(cartLines, sections);
    });
  }

  function _markAsSynced() {
    console.log('Sync status saved!');
    localStorage.setItem(config.cartSyncKey, '1');
  }

  function _updateFreeShippingBar(subtotalPrice) {
    const cartFreeShips = document.querySelectorAll('free-ship-progress-bar');
    if (cartFreeShips.length > 0) {
      cartFreeShips.forEach((item) => {
        item.init(subtotalPrice);
      });
    }
  }

  function _updateUI(items = [], sections = []) {
    const totalItems = items.reduce((total, acc) => (total += acc.quantity), 0);

    // update bubble counts
    const headerCountEls = document.querySelectorAll('.cart-count.h-count');

    headerCountEls.forEach((el) => {
      el.textContent = totalItems;
    });

    // update drawer count
    const drawerCountEls = document.querySelectorAll(
      '.cart-count.cart-count-drawer',
    );

    drawerCountEls.forEach((el) => {
      el.textContent = `(${totalItems})`;
    });

    // update sections
    _getSectionToRender().forEach(({ id, section }) => {
      if (!sections?.[section]) return;

      const sectionEl = document.getElementById(id);
      const parsedHTML = new DOMParser().parseFromString(
        sections[section],
        'text/html',
      );
      const updatedSection = parsedHTML.getElementById(id);

      if (sectionEl && updatedSection) {
        sectionEl.innerHTML = updatedSection.innerHTML;
      }
    });

    // update freeshipping bar
    const subtotal = items.reduce(
      (total, item) => total + (item.final_line_price || 0),
      0,
    );

    _updateFreeShippingBar(subtotal);
  }

  function _withInternalUpdate(fn) {
    window.__cartNotificationInternalUpdate = true;
    return fn().finally(() => {
      window.__cartNotificationInternalUpdate = false;
    });
  }

  function _isOutOfSync(itemA = [], itemB = []) {
    const map = new Map(itemA.map((r) => [_lineKey(r), r]));

    return (
      itemB.length !== itemA.length ||
      itemB.some((item) => {
        const local = map.get(_lineKey(item));
        return !local || local.quantity !== item.quantity;
      })
    );
  }

  /**
   * Validates the items in the server, filtered the validated items and returns the valid items only.
   *
   * @param {string[]} items - validate items that is ready to push in cart
   * @returns
   */
  async function _processItemsValidation(items = [], currentItems = []) {
    const cleanItems = items.map((item) => ({
      quantity: item.quantity,
      variantId: item.variantId,
      productId: item.productId,
      properties: _cleanProperties(item?.properties),
    }));

    const currentQuantities = new Map(
      currentItems.map((item) => [_lineKey(item), item.quantity]),
    );

    const itemsToValidate = cleanItems.filter(
      (item) => (currentQuantities.get(_lineKey(item)) ?? 0) < item.quantity,
    );

    if (!itemsToValidate.length) return cleanItems;

    const validatedItems = await _validateCartItems(itemsToValidate);

    const invalidKeys = new Set(
      validatedItems
        .filter((item) => item.valid === false)
        .map((item) => _lineKey(item)),
    );

    return cleanItems.filter((item) => !invalidKeys.has(_lineKey(item)));
  }

  // ─── Public API ───────────────────────────────────────────────────────────

  return {
    // ── Lifecycle ─────────────────────────────────────────────────────────────

    init: async function () {
      if (!config.enabled) {
        console.log('[CartSync] disabled via config.enabled, skipping sync.');
        return;
      }

      const localItems = this.getLocalCartItems();
      const existingShopifyCartItems = this.getExistingCartItemsFromShopify();
      const customerId = this.getCustomerId();

      console.log(
        '[CartSync] init — customerId:',
        customerId,
        '| localItems:',
        localItems,
      );

      // Sync guard between cart items and localstorage items
      const isLocalOutOfSync = _isOutOfSync(
        localItems,
        existingShopifyCartItems,
      );

      if (isLocalOutOfSync) {
        console.log(
          '[CartSync] Found existing items that are not registered from local storage! Saving items locally...',
        );
        _saveLocal(existingShopifyCartItems);
      }

      // if at some point the session expires but the sync status is still on, reset the sync status
      if (!customerId) return this.clearSyncStatus();

      // remote customer items
      const customerExistingItems = _getAccountCartItems();

      // new snapshot of local items from storage making sure it is updated
      const localStorageItem = this.getLocalCartItems();

      const localMap = new Map(localStorageItem.map((r) => [_lineKey(r), r]));

      const hasChanges =
        customerExistingItems.length !== localStorageItem.length ||
        customerExistingItems.some((item) => {
          const local = localMap.get(_lineKey(item));
          return !local || local.quantity !== item.quantity;
        });

      if (!hasChanges) {
        console.log('[CartSync] No changes detected, skipping push.');
        _emit('syncSuccess', localItems);
        return;
      }

      if (this.isSynced()) {
        if (this.shouldEnforceSync()) {
          console.log('[CartSync] Changes detected, re-syncing...');

          // re-sync the items but the source of truth will be the remote, items not existed in remote will be removed
          return this.sync(true);
        }

        return;
      }

      if (hasChanges) {
        console.log('[Cart Sync] Initial sync...');
        await this.sync(false);
      } else {
        try {
          const remoteItems = await _getAccountCartItems();
          if (remoteItems.length > 0) {
            _saveLocal(remoteItems);
            console.log(
              '[CartSync] Pulled remote cart for logged-in user.',
              remoteItems,
            );

            const validatedItems = await _processItemsValidation(remoteItems);

            // pushed to shopify cart
            _replaceShopifyCart(validatedItems);

            // update sync status
            _markAsSynced();
          }
        } catch (e) {
          console.warn('[CartSync] Could not pull remote cart on init:', e);
        }
      }

      _emit('ready', this.getLocalCartItems());
    },

    // ── Core Sync ─────────────────────────────────────────────────────────────
    /**
     * Sync local items and remote items
     * @param {boolean} overrideLocal - overrides local items, useful for logged-in users since we need to track changes to items in their account metafield and use this as a source of truth
     * @returns
     */
    sync: async function (overrideLocal = true) {
      if (!config.enabled) {
        console.warn('[CartSync] sync() ignored, controller is disabled.');
        return;
      }
      if (_syncPromise) return _syncPromise;

      _syncPromise = (async () => {
        const customerId = this.getCustomerId();
        if (!customerId) {
          console.warn('[CartSync] sync() called but no customerId found.');
          return;
        }

        _isSyncing = true;
        _emit('syncStart', null);

        try {
          const [localItems, remoteItems] = await Promise.all([
            Promise.resolve(this.getLocalCartItems()),
            _getAccountCartItems(customerId),
          ]);

          let _items = _mergeItems(localItems, remoteItems);

          // the only items we need is the remote items
          if (overrideLocal) {
            _items = remoteItems;
          }

          // validate the merged items first
          const validItems = await _processItemsValidation(
            _items,
            this.getExistingCartItemsFromShopify(),
          );

          // save items to local and remove
          _saveLocal(validItems);

          if (!overrideLocal || validItems.length !== _items.length) {
            _pushRemoteCartItems(customerId, validItems).catch((e) =>
              console.error('[CartSync] Failed to save account cart:', e),
            );
          }

          await this.pushMergedToShopifyCart({
            strategy: 'replace',
            items: validItems,
          });

          console.log('[CartSync] Sync complete.', validItems);
          _emit('syncSuccess', validItems);

          // mark as synced
          _markAsSynced();

          return validItems;
        } catch (e) {
          console.error('[CartSync] Sync failed:', e);
          _emit('syncError', e);
          throw e;
        } finally {
          _isSyncing = false;
          _syncPromise = null;
        }
      })();

      return _syncPromise;
    },

    /**
     * Checks if there's a change in user accounts cart items and return state if it should enforce syncing again
     * @returns { boolean }
     */
    shouldEnforceSync() {
      const currentItems = this.getLocalCartItems(); // local cart items is always up-to-date so we need this as a baseline

      const accountItems = _getAccountCartItems();

      // Different item count — something was added or removed on another device
      if (currentItems.length !== accountItems.length) return true;

      // local map for quick lookup
      const localMap = new Map(currentItems.map((i) => [_lineKey(i), i]));

      // Check if any account item differs from local
      return accountItems.some((accountItem) => {
        const local = localMap.get(_lineKey(accountItem));

        // New item on another device
        if (!local) return true;

        // Quantity changed on another device
        if (local.quantity !== accountItem.quantity) return true;

        return false;
      });
    },

    isSynced() {
      return localStorage.getItem(config.cartSyncKey) === '1';
    },

    clearSyncStatus() {
      localStorage.removeItem(config.cartSyncKey);
    },

    saveItems: async function (items = []) {
      const __items = _transformCartItems(items);

      _saveLocal(__items);

      // check if logged-in
      const customerId = this.getCustomerId();
      if (customerId) {
        _withInternalUpdate(async () => {
          return _pushRemoteCartItems(customerId, __items);
        });
      }
    },

    // ── Item Mutation ─────────────────────────────────────────────────────────

    /** Clear the entire cart locally */
    clearCart: async function () {
      return _withInternalUpdate(async () => {
        // clear items locally
        _saveLocal([]);

        // clear items from device registered in shopify cart items
        await _replaceShopifyCart([], true);

        this.clearSyncStatus();

        _emit('cleared', []);
      });
    },

    // ── Shopify Cart Push ─────────────────────────────────────────────────────

    /**
     * Push local cart items into the live Shopify cart.
     * strategy 'replace' (default) — clear then add all.
     * strategy 'addOnly'           — only add variants not already in the cart.
     */
    pushMergedToShopifyCart: async function ({ items } = {}) {
      if (!config.enabled) {
        console.warn(
          '[CartSync] pushMergedToShopifyCart() ignored, controller is disabled.',
        );
        return;
      }

      const cartItems = items ?? this.getLocalCartItems();

      _emit('shopifyCartPushStart', cartItems);

      try {
        await _applyShopifyCartDiff(cartItems);

        console.log('[CartSync] pushMergedToShopifyCart — done.', cartItems);
        _emit('shopifyCartSynced', cartItems);
      } catch (e) {
        console.error('[CartSync] pushMergedToShopifyCart failed:', e);
        _emit('shopifyCartPushError', e);
        throw e;
      }
    },

    // ── Remote Persistence ────────────────────────────────────────────────────

    mutate: async function (items) {
      const customerId = this.getCustomerId();
      if (!customerId) {
        console.warn('[CartSync] mutate() skipped — user not logged in.');
        return;
      }
      try {
        await _pushRemoteCartItems(customerId, items);
        _emit('mutated', items);
      } catch (e) {
        console.error('[CartSync] mutate() failed:', e);
        _emit('mutateError', e);
        throw e;
      }
    },

    // ── Reads ─────────────────────────────────────────────────────────────────
    /** Read local cart items from local storage*/
    getLocalCartItems: function () {
      try {
        return _combineLines(
          JSON.parse(localStorage.getItem(config.storageKey) ?? '[]'),
        );
      } catch {
        return [];
      }
    },

    /**
     * Reads the existing cart items from shopify and transform it into optimized object ready to saved locally and remotely
     * @returns
     */
    getExistingCartItemsFromShopify() {
      const existingCartItems = window?.shopifyUser?.existingCartItems || [];
      const transformedItems = _transformCartItems(existingCartItems);
      return transformedItems;
    },

    /** Read customer id */
    getCustomerId: function () {
      return window?.shopifyUser?.id ?? null;
    },

    /** Number of unique line items. */
    getItemCount: function () {
      return this.getLocalCartItems().length;
    },

    /** Sum of all quantities. */
    getTotalQuantity: function () {
      return this.getLocalCartItems().reduce((sum, i) => sum + i.quantity, 0);
    },

    isSyncing: function () {
      return _isSyncing;
    },

    // ── Events ────────────────────────────────────────────────────────────────

    /**
     * Subscribe to cart events.
     * Events: 'ready' | 'change' | 'syncStart' | 'syncSuccess' | 'syncError'
     *       | 'mutated' | 'mutateError' | 'cleared'
     *       | 'shopifyCartPushStart' | 'shopifyCartSynced' | 'shopifyCartPushError'
     * @returns {Function} unsubscribe
     */
    on: function (event, fn) {
      if (!_listeners[event]) _listeners[event] = [];
      _listeners[event].push(fn);
      return () => {
        _listeners[event] = _listeners[event].filter((f) => f !== fn);
      };
    },

    // ── Dev Utilities ─────────────────────────────────────────────────────────

    debug: function () {
      const items = this.getLocalCartItems();
      console.group('[CartSync] Debug');
      console.log('customerId :', this.getCustomerId());
      console.log('isSyncing  :', this.isSyncing());
      console.log('itemCount  :', this.getItemCount());
      console.log('totalQty   :', this.getTotalQuantity());
      console.table(items);
      console.groupEnd();
    },

    destroy: function () {
      localStorage.removeItem(config.storageKey);
      clearTimeout(_syncDebounceTimer);
      _listeners = {};
      _syncPromise = null;
      console.log('[CartSync] Destroyed.');
    },
  };
})();

// ─── Boot ──────────────────────────────────────────────────────────────────────

CartSyncController.init();
